/* eslint-disable filenames/match-regex */

import Company from '@/entities/Company';
import User from '@/entities/User';
import { Login2faNecessaryError } from '@/errors';
import { router } from '@/router';
import AuthService from '@/services/Auth';
import CompanyService from '@/services/Company';
import ConnectedProfileService from '@/services/ConnectedProfile';
import UserService from '@/services/User';
import { store as $rootStore } from '@/store';

const token = localStorage.getItem('token');
const hasToken = typeof token === 'string' && token.length > 0;

const selectedCompanyId = localStorage.getItem('companyId');

const state = {
  isFetching: false,
  selectedCompanyId,
  user: null,
  company: null,
  token,
  tokenReceivedAt: hasToken
    ? parseInt(localStorage.getItem('tokenReceivedAt'))
    : 0,
};

const getters = {
  isFetching(state) {
    return state.isFetching;
  },

  hasToken(state) {
    return state.token !== null;
  },

  token(state) {
    return state.token;
  },

  tokenReceivedAt(state) {
    return state.tokenReceivedAt;
  },

  loggedIn(state) {
    return state.user !== null;
  },

  user(state) {
    return state.user;
  },

  company(state) {
    return state.company;
  },

  selectedCompanyId(state) {
    if (state.selectedCompanyId) {
      return state.selectedCompanyId;
    }

    return state.user ? state.user.company.id : null;
  },
};

let fetchPromise = null;

const actions = {
  fetch(store) {
    if (fetchPromise) {
      return fetchPromise;
    }

    if (!store.getters['loggedIn']) {
      store.commit('isFetching', true);
    }

    fetchPromise = AuthService.fetch()
      .then(async user => {
        store.commit('setUser', user);

        const selectedCompanyId = store.getters['selectedCompanyId'];
        if (selectedCompanyId === null || user.company.id === selectedCompanyId) {
          await store.commit('setCompany', user.company);
        }
        else {
          await store.dispatch('fetchCompanyInBackground');
        }

        setTimeout(() => store.commit('isFetching', false), 1); // nextTick.

        return user;
      })
      .catch(error => {
        store.commit('isFetching', false);

        throw error;
      })
      .finally(() => {
        fetchPromise = null;
      });

    return fetchPromise;
  },

  async fetchCompanyInBackground(store, companyId) {
    if (!companyId) {
      companyId = store.getters['selectedCompanyId'];
    }

    const companyResp = await CompanyService.get(companyId);
    store.commit('setCompany', companyResp);
  },

  signup(store, { hash, firstname, lastname, email, contactPhone, companyName, timezone, password, newsletter, recaptchaToken, source }) {
    return AuthService.signup({ hash, firstname, lastname, email, contactPhone, companyName, timezone, password, newsletter, recaptchaToken, source }).then(resp => {
      if (resp && resp.access_token) {
        store.commit('setToken', resp.access_token);
        store.commit('setUser', resp.user);
      }

      return resp;
    });
  },

  login(store, { email, password, code, trust }) {
    return AuthService.login(email, password, code, trust).then(resp => {
      if ('2fa' in resp) {
        throw new Login2faNecessaryError();
      }

      store.commit('setToken', resp.access_token);
      store.commit('setUser', resp.user);

      return resp;
    });
  },

  refresh(store) {
    if (!store.getters['loggedIn']) {
      return Promise.reject(new Error('User is not logged in'));
    }

    return AuthService.refreshToken().then(resp => {
      store.commit('setToken', resp.access_token);

      return resp;
    });
  },

  async logout(store, payload = {}) {
    const gotoLogin = payload.gotoLogin !== undefined ? payload.gotoLogin : true;

    try {
      await AuthService.logout();
    }
    finally {
      await store.dispatch('teardown', { gotoLogin });
    }
  },

  async teardown(store, payload = {}) {
    const gotoLogin = payload.gotoLogin !== undefined ? payload.gotoLogin : true;

    store.commit('clearToken');

    if (gotoLogin) {
      await router.replace('/auth/login').catch(() => null);
    }

    store.commit('clearUser');

    $rootStore.dispatch('dashboard/reset');
    $rootStore.dispatch('rules/resetView');
    $rootStore.dispatch('creatorLite/resetView');
    $rootStore.dispatch('invites/reset');
    $rootStore.dispatch('inAppNotifications/reset');
  },

  connectProfile(store, { platform, accessToken }) {
    return ConnectedProfileService.connectProfile(platform, accessToken)
      .then(resp => {
        store.commit('addProfile', resp);
        store.dispatch('fetch');
        return resp;
      });
  },

  removeProfile(store, id) {
    return ConnectedProfileService.removeProfile(id).then(resp => {
      store.commit('deleteProfile', id);
      store.dispatch('fetch');
      return resp;
    });
  },

  updateAccount(store, details) {
    return UserService.update(store.getters['user'].id, details)
      .then(resp => {
        store.commit('setUser', resp);
        return resp;
      });
  },

  changeCompany(store, companyId) {
    store.commit('isFetching', true);

    return CompanyService.get(companyId)
      .then(company => {
        store.commit('setCompany', company);
        $rootStore.dispatch('invites/reset');
        $rootStore.dispatch('posts/resetView', true);
        store.commit('isFetching', false);
        return company;
      })
      .catch(error => {
        store.commit('isFetching', false);
        throw error;
      });
  },
};

const mutations = {
  isFetching(state, value) {
    state.isFetching = value;
  },

  clearToken(state) {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenReceivedAt');
    state.token = null;
    state.tokenReceivedAt = 0;
  },

  clearUser(state) {
    localStorage.removeItem('companyId');
    state.user = null;
    state.company = null;
    state.selectedCompanyId = null;
  },

  setToken(state, accessToken) {
    const now = new Date().getTime();

    localStorage.setItem('token', accessToken);
    localStorage.setItem('tokenReceivedAt', now);
    state.token = accessToken;
    state.tokenReceivedAt = now;
  },

  setUser(state, user) {
    if (!(user instanceof User) && typeof user === 'object') {
      user = new User(user);
    }

    if (!state.selectedCompanyId) {
      state.company = user.company;
    }

    state.user = user;
  },

  setCompany(state, company) {
    if (!(company instanceof Company) && typeof company === 'object') {
      company = new Company(company);
    }

    state.company = company;
  },

  updateCompany(state, company) {
    state.user.company.updateWith(company);
    state.company.updateWith(company);
  },

  addProfile(state, profile) {
    let profilesByMachineName = {};
    state.user.connected_profiles.forEach(p => {
      profilesByMachineName[p.machine_name] = p;
    });
    profilesByMachineName[profile.machine_name] = profile;
    state.user.connected_profiles = Object.values(profilesByMachineName);

    profilesByMachineName = {};
    state.user.company.connected_profiles.forEach(p => {
      profilesByMachineName[p.machine_name] = p;
    });
    profilesByMachineName[profile.machine_name] = profile;
    state.user.company.connected_profiles = Object.values(profilesByMachineName);

    state.company.connected_profiles = state.user.company.connected_profiles;
  },

  deleteProfile(state, id) {
    state.user.connected_profiles = state.user.connected_profiles.filter(p => p.id !== id);
    state.company.connected_profiles = state.company.connected_profiles.filter(p => p.id !== id);
  },

  selectCompany(state, value) {
    if (state.user && state.user.company.id === value) {
      value = null;
    }

    if (value === null) {
      window.localStorage.removeItem('companyId');
    }
    else {
      window.localStorage.setItem('companyId', value);
      state.selectedCompanyId = value;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
